/**
 * The store, the opt-in rewarded ads, Google Play Games and the in-app review / update of the client: the platform
 * side of the game's MonetizationPort (src/game/monetization.ts). Platform-neutral in shape: a provider per platform
 * behind the same port, the server as the only authority (it verifies every purchase and every ad view and credits
 * them; the client only runs the platform's flow and asks the server).
 *
 *  - Store: 'google-play' in the Android app (the FwpPlay plugin: Play Billing; the server verifies with the Play
 *    Developer API), 'dev-test' in development builds only (build-flags TEST_PROVIDERS) against a server that allows it
 *    (STORE_DEV_TEST). Products and amounts come from the server (store.catalog), prices from Play. A purchase: Play's
 *    flow with the obfuscated account id -> player.command 'store.redeem' -> consume.
 *    Everywhere else (the web page, an app build or a server without payments) the shop is its skeleton: the offers of
 *    the catalogue with its list prices, and buy() answers 'unavailable' without asking anybody (nothing charged,
 *    nothing credited, nothing recorded). The Google Play path takes over by itself once the app has Play Billing and
 *    the server verifies Google Play purchases.
 *    Unconsumed purchases (the app died before the credit, a pending payment completed) are redeemed at the first
 *    refresh and when Play reports them; the game reloads its state (onCredited).
 *  - Rewarded ads: AdMob in the Android app (UMP consent first; the ad unit of the build, Google's test unit in debug
 *    builds), 'dev-test' in development builds. An ad is loaded ahead so the opt-in button appears only when one can
 *    be shown; ads.start -> the ad with the ticket's server-side verification options -> the server grants the reward
 *    on AdMob's signed callback (debug builds: test ads do not call the server, so their tickets are the 'dev-test'
 *    provider's and are verified with ads.testReward) -> ads.ticket until granted.
 *    Everywhere else (the web page, an app build without an ad unit, a server that does not verify this client's ads)
 *    the ads are their skeleton: state() answers 'soon' (a button the game shows on a phone says ads are coming) and
 *    nothing is asked of the server. AdMob takes over by itself once the build has its ad unit and the server verifies
 *    AdMob's callbacks.
 *  - Play Games: optional link of the game's account (auth.linkPlayGames with a server auth code) and the sign-in on a
 *    new device (auth.playGames); achievements and leaderboards are fed from the game's own command results
 *    (installPlayGamesHooks). In-app review at good moments, rate-limited; a flexible in-app update when Play has one.
 * Every failure is quiet: the buttons disappear, the game goes on.
 */
import type { AdContext, AdPlacementId, AdWatchResult, MonetizationPort, PlayGamesPort, RewardedAdsPort, StoreBuyResult, StoreOffer, StorePort } from '../../game/runtime/monetization';
import type { AdsStatusResult, StoreCatalogResult, StoreProviderId } from '../../shared/protocol/model';
import type { GameServices } from '../game-services';
import type { PlayApi, PlayCapabilities, PlayPurchase } from './play';

/** The build's Google Play IDs (Vite variables written by scripts/android.mjs; empty on the web). */
export interface PlayBuildConfig {
  /** The rewarded ad unit ('' = no ads). */
  admobRewardedId: string;
  /** Debug build: test ads, whose views the server verifies through the 'dev-test' provider. */
  adsTesting: boolean;
  consentDebugGeography: 'eea' | 'not_eea' | null;
  playGames: boolean;
  playGamesServerClientId: string;
  /** Play Games ids: achievements / leaderboards by the game's key (docs/google-play.md). */
  playGamesIds: { achievements?: Record<string, string>; leaderboards?: Record<string, string> };
}

export function playBuildConfig(env: Record<string, string | undefined>): PlayBuildConfig {
  let ids: PlayBuildConfig['playGamesIds'] = {};
  try {
    ids = env.VITE_FWP_PLAY_GAMES_IDS ? JSON.parse(env.VITE_FWP_PLAY_GAMES_IDS) : {};
  } catch {
    ids = {};
  }
  const geo = env.VITE_FWP_CONSENT_DEBUG_GEOGRAPHY;
  return {
    admobRewardedId: (env.VITE_FWP_ADMOB_REWARDED_ID ?? '').trim(),
    adsTesting: env.VITE_FWP_ADS_TESTING === '1',
    consentDebugGeography: geo === 'eea' || geo === 'not_eea' ? geo : null,
    playGames: env.VITE_FWP_PLAY_GAMES === '1',
    playGamesServerClientId: (env.VITE_FWP_PLAY_GAMES_SERVER_CLIENT_ID ?? '').trim(),
    playGamesIds: ids && typeof ids === 'object' ? ids : {},
  };
}

export interface MonetizationEnv {
  services: GameServices;
  /** The FwpPlay plugin (Android app), or null. */
  play: PlayApi | null;
  /** Development build: the 'dev-test' providers may be used (build-flags TEST_PROVIDERS). */
  testProviders: boolean;
  /**
   * Development hook (`?store=preview`): the store and the rewarded ads never use the 'dev-test' provider, as on the
   * web page (their skeletons).
   */
  storePreview?: boolean;
  config: PlayBuildConfig;
  /** Asks the developer whether a test ad counts as watched (development builds without the Android plugin). */
  confirmTestAd?: () => Promise<boolean>;
  /** Small persistent values (the review's rate limit). */
  storage?: { get(key: string): string | null; set(key: string, value: string): void };
  /** Restarts the game (after a Play Games sign-in switched the account). */
  restart?: () => void;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
}

const REVIEW_KEY = 'fwp.review.lastAsked';
/** In-app review: at most once per 30 days (Google Play also limits it on its side). */
const REVIEW_INTERVAL_MS = 30 * 24 * 3600_000;
/** How long watch() waits for the server's grant after the ad. */
const GRANT_WAIT_MS = 30_000;

function randomSuffix(): string {
  const bytes = new Uint8Array(12);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function reasonOf(err: unknown): string {
  const e = err as { details?: { reason?: unknown }; code?: unknown; message?: unknown };
  return typeof e?.details?.reason === 'string' ? e.details.reason : typeof e?.code === 'string' ? e.code : String(e?.message ?? err);
}

/** The port plus what the host drives: `dispose` at the end. */
export type MonetizationHost = MonetizationPort & { dispose(): void };

export function createMonetization(env: MonetizationEnv): MonetizationHost {
  const { services, play } = env;
  const now = env.now ?? (() => Date.now());
  const sleep = env.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const features = () => services.serverInfo?.features ?? [];
  let caps: PlayCapabilities | null = null;
  const capsReady: Promise<PlayCapabilities | null> = play ? play.capabilities().then((c) => (caps = c), () => null) : Promise.resolve(null);

  // ------------------------------------------------------------------------------------------------ store
  const creditedListeners = new Set<() => void>();
  let catalog: StoreCatalogResult | null = null;
  let prices = new Map<string, string>();
  let storeProvider: StoreProviderId | null = null;
  let recovered = false;
  const redeeming = new Set<string>();

  const pickStoreProvider = (c: StoreCatalogResult): StoreProviderId | null => {
    if (play && caps?.billing && c.providers.includes('google-play')) return 'google-play';
    if (env.testProviders && !env.storePreview && c.providers.includes('dev-test')) return 'dev-test';
    return null;
  };

  const redeem = async (provider: StoreProviderId, productId: string, purchaseToken: string, orderId: string | null) => {
    const args: Record<string, string> = { provider, productId, purchaseToken };
    if (orderId) args.orderId = orderId;
    return services.command('store.redeem', args);
  };

  /** Redeems a purchase Play reported outside a buy() (recovery, a completed pending payment). */
  const redeemLoose = async (p: PlayPurchase) => {
    if (p.purchaseState !== 'purchased' || redeeming.has(p.purchaseRef)) return;
    redeeming.add(p.purchaseRef);
    try {
      const out = await redeem('google-play', p.productId, p.purchaseToken, p.orderId);
      const r = out.result as { status?: string; consume?: boolean };
      if (r.consume) await play!.consume(p.purchaseRef).catch(() => undefined);
      if (r.status === 'credited') for (const l of [...creditedListeners]) l();
    } catch (err) {
      // Pending, refunded, another account's: nothing to do on the device; the server keeps the record.
      if (['purchase_voided', 'purchase_other_account', 'purchase_invalid'].includes(reasonOf(err))) await play!.consume(p.purchaseRef).catch(() => undefined);
    } finally {
      redeeming.delete(p.purchaseRef);
    }
  };

  if (play) play.onPurchasesUpdated((list) => void Promise.all(list.map(redeemLoose)));

  const store: StorePort = {
    offers() {
      if (!catalog) return null;
      const out: StoreOffer[] = [];
      for (const p of catalog.products) {
        if (!p.available) continue;
        // Play's localized price; without Play (the development provider, the skeleton) the game shows the list price.
        const price = storeProvider === 'google-play' ? prices.get(p.productId) ?? null : null;
        // A product Play does not know (not created in Play Console, or not active yet) is not offered.
        if (storeProvider === 'google-play' && price === null) continue;
        out.push({
          productId: p.productId,
          kind: p.kind,
          gold: p.gold,
          diam: p.diam,
          items: { ...p.items },
          multiplier: p.multiplier,
          price,
          listPrices: { ...(p.listPrices ?? {}) },
          bonusPct: p.bonusPct ?? 0,
          shop: p.shop,
          label: p.label,
          availableUntil: p.availableUntil,
        });
      }
      return out;
    },
    payment() {
      return storeProvider === 'google-play' ? 'store' : storeProvider === 'dev-test' ? 'test' : null;
    },
    refresh(changed) {
      void (async () => {
        await capsReady;
        if (!features().includes('store')) {
          catalog = null;
          storeProvider = null;
          return;
        }
        try {
          catalog = await services.request('store.catalog', {});
        } catch {
          catalog = null;
        }
        storeProvider = catalog ? pickStoreProvider(catalog) : null;
        if (catalog && storeProvider === 'google-play') {
          try {
            const r = await play!.queryProducts(catalog.products.map((p) => p.productId));
            prices = new Map(r.products.map((p) => [p.productId, p.price]));
          } catch {
            prices = new Map();
          }
          if (!recovered) {
            recovered = true;
            const owned = await play!.queryPurchases().catch(() => ({ purchases: [] as PlayPurchase[] }));
            for (const p of owned.purchases) await redeemLoose(p);
          }
        }
        changed?.();
      })();
    },
    async buy(productId): Promise<StoreBuyResult> {
      const provider = storeProvider;
      if (!catalog) return { status: 'failed', reason: 'store_unavailable' };
      // The skeleton of the shop: no payment here yet. Nobody is asked, nothing is recorded.
      if (!provider) return { status: 'unavailable' };
      try {
        if (provider === 'dev-test') {
          const out = await redeem('dev-test', productId, `devtest.${randomSuffix()}`, null);
          return { status: 'credited', outcome: out, result: out.result as Record<string, any> };
        }
        const r = await play!.purchase(productId, catalog.obfuscatedAccountId);
        if (r.status === 'cancelled') return { status: 'cancelled' };
        if (r.status === 'pending') return { status: 'pending' };
        if (r.status === 'error') return { status: 'failed', reason: r.code ?? 'billing_error' };
        const purchase = r.purchases.find((p) => p.productId === productId && p.purchaseState === 'purchased');
        if (!purchase) return r.purchases.some((p) => p.purchaseState === 'pending') ? { status: 'pending' } : { status: 'failed', reason: 'no_purchase' };
        redeeming.add(purchase.purchaseRef);
        try {
          const out = await redeem('google-play', productId, purchase.purchaseToken, purchase.orderId);
          if ((out.result as { consume?: boolean }).consume) await play!.consume(purchase.purchaseRef).catch(() => undefined);
          return { status: 'credited', outcome: out, result: out.result as Record<string, any> };
        } finally {
          redeeming.delete(purchase.purchaseRef);
        }
      } catch (err) {
        const reason = reasonOf(err);
        return reason === 'purchase_pending' ? { status: 'pending' } : { status: 'failed', reason };
      } finally {
        store.refresh();
      }
    },
    onCredited(listener) {
      creditedListeners.add(listener);
      return () => creditedListeners.delete(listener);
    },
  };

  // ------------------------------------------------------------------------------------------------ rewarded ads
  let adStatus: AdsStatusResult | null = null;
  /** Which server provider this client's ads count for: 'admob' (real ads) or 'dev-test' (test ads, dev builds). */
  let adProvider: 'admob' | 'dev-test' | null = null;
  /**
   * The native AdMob path: consent asked, SDK started, an ad loaded. `consent` null: not known (not asked yet, or the
   * UMP request failed, e.g. offline: 'unavailable', asked again at the next refresh, never reported as 'declined').
   */
  let consent: { canRequestAds: boolean; privacyOptionsRequired: boolean } | null = null;
  let consentFailed = false;
  let adsStarted = false;
  let adLoaded = false;
  let adLoading: Promise<void> | null = null;
  let consentAsked: Promise<void> | null = null;

  const nativeAds = () => Boolean(play && caps?.ads && env.config.admobRewardedId && (caps.adsConfigured || env.config.adsTesting));
  /** The simulated ad of the dev-test provider (development builds, unless `?store=preview`). */
  const devAds = () => env.testProviders && !env.storePreview;
  /** Whether this client could show a rewarded ad at all (else the skeleton: state() 'soon', no request). */
  const adsPossible = () => nativeAds() || devAds();
  /** The plugin's capabilities arrived (the app), or there is no plugin. */
  let capsKnown = !play;
  void capsReady.then(() => (capsKnown = true));
  /** ads.status was asked since the start (refresh), and the server has the feature. */
  let adsChecked = false;
  let adsFeature = false;

  const pickAdProvider = (s: AdsStatusResult): 'admob' | 'dev-test' | null => {
    if (nativeAds()) {
      if (env.config.adsTesting) return env.testProviders && s.providers.includes('dev-test') ? 'dev-test' : null;
      return s.providers.includes('admob') ? 'admob' : null;
    }
    // A development build without the Android plugin: the simulated ad of the dev-test provider.
    return devAds() && s.providers.includes('dev-test') ? 'dev-test' : null;
  };

  const ensureConsent = () => {
    if (consentFailed) {
      consentFailed = false;
      consentAsked = null;
    }
    consentAsked ??= (async () => {
      try {
        const c = await play!.adsConsent(env.config.consentDebugGeography ? { debugGeography: env.config.consentDebugGeography } : {});
        consent = { canRequestAds: c.canRequestAds, privacyOptionsRequired: c.privacyOptionsRequired };
        if (c.canRequestAds && !adsStarted) {
          await play!.adsInitialize();
          adsStarted = true;
        }
      } catch {
        // (the player refused nothing: no button for now, the line of a refused consent is not shown either)
        consent = null;
        consentFailed = true;
      }
    })();
    return consentAsked;
  };

  const loadAd = () => {
    if (adLoaded || adLoading || !adsStarted) return adLoading ?? Promise.resolve();
    adLoading = play!
      .rewardedLoad(env.config.admobRewardedId)
      .then(
        (r) => {
          adLoaded = r.loaded;
        },
        () => {
          adLoaded = false; // no fill, offline: the buttons stay hidden
        },
      )
      .finally(() => {
        adLoading = null;
      });
    return adLoading;
  };

  const remaining = (placement: AdPlacementId) => {
    const p = adStatus?.placements.find((x) => x.placement === placement);
    return p && p.enabled ? Math.min(p.remainingToday, adStatus!.remainingToday) : 0;
  };

  const ads: RewardedAdsPort = {
    offered(placement) {
      return ads.state(placement) === 'ready';
    },
    state(placement) {
      if (!capsKnown) return 'unavailable';
      // The skeleton: no ads in this client (the web page, no ad unit), or the server has none / does not verify ours.
      if (!adsPossible()) return 'soon';
      if (!adsChecked) return 'unavailable';
      if (!adsFeature) return 'soon';
      if (!adStatus) return 'unavailable'; // (ads.status failed: offline)
      if (!adProvider) return 'soon';
      const p = adStatus.placements.find((x) => x.placement === placement);
      if (!p || !p.enabled) return 'unavailable';
      if (remaining(placement) <= 0) return 'capped';
      if (!nativeAds()) return 'ready';
      if (!consent) return 'unavailable';
      if (!consent.canRequestAds) return 'declined';
      return adLoaded ? 'ready' : 'unavailable';
    },
    refresh(changed) {
      void (async () => {
        await capsReady;
        // The skeleton asks nobody (but its 'soon' is news to a screen drawn before the plugin's capabilities came).
        if (!adsPossible()) {
          changed?.();
          return;
        }
        adsChecked = true;
        adsFeature = features().includes('ads');
        if (!adsFeature) {
          adStatus = null;
          adProvider = null;
          changed?.();
          return;
        }
        try {
          adStatus = await services.request('ads.status', {});
        } catch {
          adStatus = null;
        }
        adProvider = adStatus ? pickAdProvider(adStatus) : null;
        if (adProvider && nativeAds()) {
          await ensureConsent();
          await loadAd();
        }
        changed?.();
      })();
    },
    async watch(placement: AdPlacementId, context?: AdContext): Promise<AdWatchResult> {
      const provider = adProvider;
      if (!provider || !ads.offered(placement)) return { status: 'failed', reason: 'unavailable' };
      let ticket;
      try {
        ticket = (await services.request('ads.start', { placement, provider, ...(context ? { context } : {}) })).ticket;
      } catch (err) {
        ads.refresh();
        return { status: 'failed', reason: reasonOf(err) };
      }
      try {
        if (nativeAds()) {
          adLoaded = false;
          const shown = await play!.rewardedShow(ticket.ssv);
          void loadAd();
          if (!shown.rewarded) return { status: 'cancelled' };
        } else if (!(await (env.confirmTestAd ?? (async () => true))())) {
          return { status: 'cancelled' };
        }
        if (provider === 'dev-test') await services.request('ads.testReward', { ticketId: ticket.ticketId });
        const deadline = now() + GRANT_WAIT_MS;
        for (let wait = 500; ; wait = Math.min(2000, wait * 2)) {
          const t = (await services.request('ads.ticket', { ticketId: ticket.ticketId })).ticket;
          if (t.status === 'granted') return { status: 'granted', result: (t.result ?? {}) as Record<string, any> };
          if (t.status === 'failed' || t.status === 'expired') return { status: 'failed', reason: t.failure ?? t.status };
          if (now() >= deadline) return { status: 'delayed' };
          await sleep(wait);
        }
      } catch (err) {
        return { status: 'failed', reason: reasonOf(err) };
      } finally {
        ads.refresh();
      }
    },
    maxAmount(placement) {
      return adStatus?.placements.find((x) => x.placement === placement)?.maxAmount ?? null;
    },
    privacyOptionsRequired() {
      return Boolean(consent?.privacyOptionsRequired);
    },
    async showPrivacyOptions() {
      if (!play) return;
      await play.adsPrivacyOptions().catch(() => undefined);
      consentAsked = null;
      await ensureConsent();
    },
  };

  // ------------------------------------------------------------------------------------------------ Play Games
  let pgLinked: boolean | null = null;
  const pgAvailable = () => Boolean(play && caps?.playGames && env.config.playGames && env.config.playGamesServerClientId && features().includes('playGames'));
  const serverCode = async (): Promise<string | null> => {
    const st = await play!.gamesStatus();
    if (!st.authenticated && !(await play!.gamesSignIn()).authenticated) return null;
    return (await play!.gamesServerAuthCode(env.config.playGamesServerClientId)).code;
  };
  const playGames: PlayGamesPort = {
    available: pgAvailable,
    linked: () => pgLinked,
    refresh(changed) {
      void (async () => {
        await capsReady;
        if (!pgAvailable()) return;
        pgLinked = await services.request('auth.playGamesStatus', {}).then(
          (r) => r.linked,
          () => null,
        );
        changed?.();
      })();
    },
    async link() {
      if (!pgAvailable()) return 'failed';
      try {
        const code = await serverCode();
        if (!code) return 'failed';
        pgLinked = (await services.request('auth.linkPlayGames', { serverAuthCode: code })).linked;
        return pgLinked ? 'linked' : 'failed';
      } catch {
        return 'failed';
      }
    },
    async signIn() {
      if (!pgAvailable()) return 'failed';
      try {
        const code = await serverCode();
        if (!code) return 'failed';
        await services.signInWithPlayGames(code);
        env.restart?.();
        return 'signed_in';
      } catch (err) {
        return reasonOf(err) === 'play_games_not_linked' ? 'not_linked' : 'failed';
      }
    },
    showAchievements() {
      if (pgAvailable()) void play!.gamesShowAchievements().catch(() => undefined);
    },
  };

  // ------------------------------------------------------------------------------------------------ review, updates
  const goodMoment = (kind: 'victory' | 'level_up') => {
    void kind;
    if (!play || !caps?.review || caps.installer !== 'com.android.vending') return;
    const last = Number(env.storage?.get(REVIEW_KEY) ?? 0) || 0;
    if (now() - last < REVIEW_INTERVAL_MS) return;
    env.storage?.set(REVIEW_KEY, String(now()));
    void play.requestReview().catch(() => undefined);
  };

  return {
    // The store and the ads everywhere (their skeletons without payments / ads); Play Games only where it can be.
    store,
    ads,
    playGames: play ? playGames : null,
    goodMoment,
    dispose() {
      creditedListeners.clear();
    },
  };
}

/**
 * A flexible in-app update (Google Play): when Play has a newer version, it downloads in the background and is
 * installed at the next start of the game (completeFlexibleUpdate while nothing is running). Never blocks the game.
 */
export async function checkForUpdate(play: PlayApi | null): Promise<void> {
  if (!play) return;
  try {
    const caps = await play.capabilities();
    if (!caps.updates || caps.installer !== 'com.android.vending') return;
    const info = await play.updateInfo();
    if (info.installStatus === 'downloaded') {
      await play.completeFlexibleUpdate();
      return;
    }
    if (info.available && info.flexibleAllowed) await play.startFlexibleUpdate();
  } catch {
    /* no update now */
  }
}

/**
 * Play Games achievements and leaderboards from the game's own events (the ids of the build: PlayBuildConfig
 * .playGamesIds; docs/google-play.md lists the keys): every successful command result passes through `onCommand`.
 * Achievements: first_win (a won solo battle), level_10 (level 10 reached), achievement_claimed (a game achievement's
 * bonus claimed), supporter (a purchase credited). Leaderboard: level (the player's level after a won battle).
 * A key without an id in the build does nothing.
 */
export function playGamesHooks(play: PlayApi | null, config: PlayBuildConfig) {
  const achievements = config.playGamesIds.achievements ?? {};
  const leaderboards = config.playGamesIds.leaderboards ?? {};
  const unlock = (key: string) => {
    const id = achievements[key];
    if (play && id) void play.gamesUnlockAchievement(id).catch(() => undefined);
  };
  const score = (key: string, value: number) => {
    const id = leaderboards[key];
    if (play && id && Number.isFinite(value)) void play.gamesSubmitScore(id, Math.floor(value)).catch(() => undefined);
  };
  return {
    /** A command the game ran and its result (player.command op). */
    onCommand(op: string, result: any): void {
      if (!play || !config.playGames) return;
      if (op === 'battle.finish' && result?.win) {
        unlock('first_win');
        if (typeof result.level === 'number') score('level', result.level);
      }
      if (op === 'battle.finish' && result?.endGame?.levels_upg > 0) {
        if (typeof result.level === 'number' && result.level >= 10) unlock('level_10');
      }
      if (op === 'store.redeem' && result?.status === 'credited') unlock('supporter');
      if (op === 'achiev.claim') unlock('achievement_claimed');
    },
  };
}
